/**
 * Plugin Editor Type Definitions for Monaco Editor
 *
 * SYNC REQUIREMENTS: This file contains string representations of TypeScript interfaces
 * that must be kept in sync with the actual implementations. Each interface here has
 * corresponding implementations that must match exactly.
 *
 * SYNC SOURCES:
 * - EventPluginData: apps/website/src/@fuse/recoil/pluginStudio/types.ts
 * - UserPluginData: apps/website/src/@fuse/recoil/pluginStudio/types.ts
 * - BoundPluginApi: apps/website/src/@fuse/api/pluginDataApi.ts
 * - pluginStyles: apps/website/src/@fuse/utils/pluginStyles.ts
 * - pluginComponents: apps/website/src/@fuse/utils/pluginComponents.tsx
 */
import {
  EventPluginData,
  UserPluginData,
} from "@fuse/recoil/pluginStudio/types"

/**
 * Converts TypeScript interface into a string representation for Monaco editor
 *
 * SYNC REQUIREMENT: These strings must match the actual TypeScript interfaces exactly
 */
export const getPluginTypeDefinitions = () => {
  /**
   * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - EventPluginData interface
   * @type {EventPluginData}
   */
  const eventPluginDataType = `
    settings?: {
      enabled: boolean
      [key: string]: any
    }
    state?: {
      [key: string]: any
    }
    [key: string]: any
  `

  /**
   * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - UserPluginData interface
   * @type {UserPluginData}
   */
  const userPluginDataType = `
    preferences?: {
      [key: string]: any
    }
    permissions?: string[]
    state?: {
      [key: string]: any
    }
    [key: string]: any
  `

  return {
    eventPluginDataType,
    userPluginDataType,
  }
}

/**
 * Generates the complete type definitions for the Monaco editor
 *
 * SYNC REQUIREMENTS: Each interface in this string must match the corresponding
 * real implementation in the codebase
 */
export const generatePluginTypeDefinitions = () => {
  const { eventPluginDataType, userPluginDataType } = getPluginTypeDefinitions()

  return `
    // Plugin API type definitions
    declare namespace PluginAPI {
      /**
       * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - EventPluginData interface
       */
      interface EventPluginData {
        ${eventPluginDataType}
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - UserPluginData interface
       */
      interface UserPluginData {
        ${userPluginDataType}
      }
      
      interface PluginConfig {
        [key: string]: any;
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - EventData interface
       * Note: This is a simplified version for plugins - the full EventData interface is more complex
       */
      interface EventData {
        id?: string;
        title?: string;
        date?: string | Date;
        location?: {
          name?: string;
          address?: string;
          coordinates?: {
            latitude: number;
            longitude: number;
          };
          [key: string]: any;
        };
        description?: string;
        attendees?: {
          count?: number;
          list?: Array<{
            id: string;
            name?: string;
            role?: string;
            [key: string]: any;
          }>;
        };
        startTime?: string | Date;
        endTime?: string | Date;
        timezone?: string;
        status?: "scheduled" | "active" | "completed" | "cancelled";
        organizer?: {
          id: string;
          name?: string;
          [key: string]: any;
        };
        settings?: Record<string, any>;
        [key: string]: any;
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/recoil/pluginStudio/types.ts - UserData interface
       */
      interface UserData {
        displayName?: string;
        email?: string;
        preferences?: {
          [key: string]: any;
        };
        [key: string]: any;
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/api/pluginDataApi.ts - BoundPluginApi interface
       * API methods exposed to the plugin
       */
      interface API {
        getPluginConfig(): Promise<PluginConfig>;
        savePluginConfig(config: any): Promise<void>;
        getPluginEventInstanceData(): Promise<EventPluginData>;
        savePluginEventInstanceData(data: any): Promise<void>;
        getEventPluginUserData(): Promise<UserPluginData>;
        saveEventPluginUserData(data: any): Promise<void>;
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/utils/pluginStyles.ts - pluginStyles object structure
       * Theme and styling utilities based on our actual plugin styles
       */
      interface Theme {
        colors: {
          primary: string;      // "#3b82f6" (blue-500)
          secondary: string;    // "#6b7280" (gray-500)
          success: string;      // "#10b981" (green-500)
          danger: string;       // "#ef4444" (red-500)
          warning: string;      // "#f59e0b" (amber-500)
          info: string;         // "#3b82f6" (blue-500)
          light: string;        // "#f3f4f6" (gray-100)
          dark: string;         // "#1f2937" (gray-800)
        };
        text: {
          title: string;        // "text-2xl font-bold text-gray-900 mb-4"
          subtitle: string;     // "text-xl font-semibold text-gray-800 mb-3"
          body: string;         // "text-base text-gray-700"
          small: string;        // "text-sm text-gray-500"
          link: string;         // "text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
        };
        layout: {
          container: string;    // "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          section: string;      // "py-6"
          card: string;         // "bg-white shadow rounded-lg p-6"
          divider: string;      // "border-t border-gray-200 my-6"
          flexRow: string;      // "flex flex-row items-center"
          flexCol: string;      // "flex flex-col"
          grid: string;         // "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        };
        form: {
          label: string;        // "block text-sm font-medium text-gray-700 mb-1"
          input: string;        // "w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          select: string;       // "w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          checkbox: string;     // "rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          error: string;        // "mt-1 text-sm text-red-600"
          group: string;        // "mb-4"
        };
        utils: {
          p: string;            // "p-4"
          px: string;           // "px-4"
          py: string;           // "py-4"
          m: string;            // "m-4"
          mx: string;           // "mx-4"
          my: string;           // "my-4"
          rounded: string;      // "rounded-lg"
          shadow: string;       // "shadow-md"
          center: string;       // "flex items-center justify-center"
          between: string;      // "flex items-center justify-between"
        };
      }
      
      /**
       * SYNC WITH: apps/website/src/@fuse/utils/pluginComponents.tsx - pluginComponents object
       * Common UI components available to plugins
       */
      namespace Components {
        interface ButtonProps {
          children: React.ReactNode;
          onClick?: () => void;
          variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'outline' | 'link';
          size?: 'small' | 'medium' | 'large';
          disabled?: boolean;
          fullWidth?: boolean;
          className?: string;
        }
        const Button: React.FC<ButtonProps>;
        
        interface AppButtonProps {
          children: React.ReactNode;
          onClick?: () => void;
          disabled?: boolean;
          fullWidth?: boolean;
          className?: string;
        }
        const AppButton: React.FC<AppButtonProps>;
        
        interface EventHeaderProps {
          title?: string;
          children: React.ReactNode;
          className?: string;
        }
        const EventHeader: React.FC<EventHeaderProps>;
        
        interface ContentPanelProps {
          children: React.ReactNode;
          title?: string;
          className?: string;
        }
        const ContentPanel: React.FC<ContentPanelProps>;
        
        interface TextAreaProps {
          label?: string;
          value: string;
          onChange: (value: string) => void;
          placeholder?: string;
          rows?: number;
          error?: string;
          disabled?: boolean;
          className?: string;
        }
        const TextArea: React.FC<TextAreaProps>;
        
        interface TextInputProps {
          label?: string;
          value: string;
          onChange: (value: string) => void;
          placeholder?: string;
          type?: string;
          error?: string;
          disabled?: boolean;
          className?: string;
        }
        const TextInput: React.FC<TextInputProps>;
        
        interface CheckboxProps {
          label?: string;
          checked: boolean;
          onChange: (checked: boolean) => void;
          disabled?: boolean;
          className?: string;
        }
        const Checkbox: React.FC<CheckboxProps>;
        
        interface CardProps {
          children: React.ReactNode;
          title?: string;
          footer?: React.ReactNode;
          className?: string;
        }
        const Card: React.FC<CardProps>;
        
        interface AvatarProps {
          src?: string;
          alt?: string;
          size?: 'small' | 'medium' | 'large';
          className?: string;
        }
        const Avatar: React.FC<AvatarProps>;
        
        interface BadgeProps {
          children: React.ReactNode;
          variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
          className?: string;
        }
        const Badge: React.FC<BadgeProps>;
        
        interface AlertProps {
          children: React.ReactNode;
          type?: 'info' | 'success' | 'warning' | 'error';
          onClose?: () => void;
          className?: string;
        }
        const Alert: React.FC<AlertProps>;
        
        interface SpinnerProps {
          size?: 'small' | 'medium' | 'large';
          className?: string;
        }
        const Spinner: React.FC<SpinnerProps>;
        
        interface TabsProps {
          tabs: Array<{ id: string; label: string; }>;
          activeTab: string;
          onChange: (tabId: string) => void;
          className?: string;
        }
        const Tabs: React.FC<TabsProps>;
      }
    }
    
    // Global variables available to plugins
    declare var api: PluginAPI.API;
    declare var theme: PluginAPI.Theme;
    declare var components: PluginAPI.Components;
    declare var eventData: PluginAPI.EventData;
    declare var userData: PluginAPI.UserData;
    
    // Global plugin function
    declare function PluginComponent(props: {}): React.ReactElement;
  `
}
